const DEFAULT_READ_TIMEOUT_MS = 10000;
const DEFAULT_WRITE_TIMEOUT_MS = 15000;
const DEFAULT_READ_RETRIES = 2;
const RETRYABLE_STATUSES = new Set([408, 425, 429, 500, 502, 503, 504]);
// 429 means "you are asking too fast". Retrying it on the normal schedule
// multiplies the very load that caused it — a request that fans out server-side
// gets its whole fan-out replayed on every attempt. Allow a single retry, and
// only after the delay the server asked for.
const RATE_LIMITED_STATUS = 429;
const RATE_LIMITED_MAX_RETRIES = 1;
const RATE_LIMITED_FALLBACK_DELAY_MS = 2000;

const inFlightReads = new Map();

export class ChatRequestError extends Error {
  constructor(message, { code, cause, url } = {}) {
    super(message);
    this.name = "ChatRequestError";
    this.code = code;
    this.cause = cause;
    this.url = url;
  }
}

const abortableDelay = (delayMs, signal) =>
  new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(
        new ChatRequestError("Richiesta annullata", {
          code: "CHAT_REQUEST_ABORTED",
        }),
      );
      return;
    }

    const cleanup = () => signal?.removeEventListener("abort", onAbort);
    const timer = setTimeout(() => {
      cleanup();
      resolve();
    }, delayMs);
    const onAbort = () => {
      clearTimeout(timer);
      cleanup();
      reject(
        new ChatRequestError("Richiesta annullata", {
          code: "CHAT_REQUEST_ABORTED",
        }),
      );
    };
    signal?.addEventListener("abort", onAbort, { once: true });
  });

const retryDelayMs = (attempt) =>
  Math.min(3000, 400 * Math.pow(2, attempt)) + Math.floor(Math.random() * 200);

const getRetryAfterMs = (response) => {
  const retryAfter = response?.headers?.get?.("retry-after");
  if (!retryAfter) return null;

  const seconds = Number(retryAfter);
  if (Number.isFinite(seconds)) return Math.max(0, seconds * 1000);

  const date = Date.parse(retryAfter);
  return Number.isNaN(date) ? null : Math.max(0, date - Date.now());
};

const requestKey = (url, config) => {
  const headers = new Headers(config.headers || {});
  return [
    String(config.method || "GET").toUpperCase(),
    String(url),
    headers.get("authorization") || "",
  ].join(" ");
};

const executeAttempt = async (url, config, timeoutMs) => {
  const controller = new AbortController();
  const externalSignal = config.signal;
  let timedOut = false;

  const forwardAbort = () => controller.abort();
  if (externalSignal?.aborted) controller.abort();
  externalSignal?.addEventListener("abort", forwardAbort, { once: true });

  const timeout = setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, timeoutMs);

  try {
    return await fetch(url, { ...config, signal: controller.signal });
  } catch (error) {
    if (timedOut) {
      throw new ChatRequestError(
        "La connessione sta impiegando troppo tempo. Riprova.",
        { code: "CHAT_REQUEST_TIMEOUT", cause: error, url: String(url) },
      );
    }

    if (externalSignal?.aborted) {
      throw new ChatRequestError("Richiesta annullata", {
        code: "CHAT_REQUEST_ABORTED",
        cause: error,
        url: String(url),
      });
    }

    throw new ChatRequestError("Connessione non disponibile. Riprova.", {
      code: "CHAT_REQUEST_NETWORK",
      cause: error,
      url: String(url),
    });
  } finally {
    clearTimeout(timeout);
    externalSignal?.removeEventListener("abort", forwardAbort);
  }
};

const executeWithRetry = async (
  url,
  config,
  { retries, timeoutMs },
) => {
  let attempt = 0;
  let rateLimitedAttempts = 0;

  while (true) {
    try {
      const response = await executeAttempt(url, config, timeoutMs);
      const isRateLimited = response.status === RATE_LIMITED_STATUS;
      const budget = isRateLimited
        ? Math.min(retries, RATE_LIMITED_MAX_RETRIES)
        : retries;
      const used = isRateLimited ? rateLimitedAttempts : attempt;

      if (used < budget && RETRYABLE_STATUSES.has(response.status)) {
        const delayMs = isRateLimited
          ? getRetryAfterMs(response) ?? RATE_LIMITED_FALLBACK_DELAY_MS
          : getRetryAfterMs(response) ?? retryDelayMs(attempt);
        if (isRateLimited) rateLimitedAttempts += 1;
        attempt += 1;
        await abortableDelay(delayMs, config.signal);
        continue;
      }
      return response;
    } catch (error) {
      const retryable =
        error?.code === "CHAT_REQUEST_TIMEOUT" ||
        error?.code === "CHAT_REQUEST_NETWORK";
      if (!retryable || attempt >= retries) throw error;

      const delayMs = retryDelayMs(attempt);
      attempt += 1;
      await abortableDelay(delayMs, config.signal);
    }
  }
};

/**
 * Fetch wrapper for chat endpoints.
 *
 * Reads are bounded, retried, and deduplicated per authenticated user. Writes
 * are bounded but never retried automatically because they may not be
 * idempotent.
 */
export const chatFetch = async (url, config = {}, options = {}) => {
  const method = String(config.method || "GET").toUpperCase();
  const isRead = method === "GET" || method === "HEAD";
  const timeoutMs =
    options.timeoutMs ??
    (isRead ? DEFAULT_READ_TIMEOUT_MS : DEFAULT_WRITE_TIMEOUT_MS);
  const retries = options.retries ?? (isRead ? DEFAULT_READ_RETRIES : 0);

  if (!isRead || options.dedupe === false || config.signal) {
    return executeWithRetry(url, config, { retries, timeoutMs });
  }

  const key = requestKey(url, config);
  if (!inFlightReads.has(key)) {
    const pending = executeWithRetry(url, config, {
      retries,
      timeoutMs,
    }).finally(() => {
      if (inFlightReads.get(key) === pending) inFlightReads.delete(key);
    });
    inFlightReads.set(key, pending);
  }

  const response = await inFlightReads.get(key);
  return response.clone();
};

export const __resetChatRequestStateForTests = () => {
  inFlightReads.clear();
};
